/* eslint-disable ordered-imports/ordered-imports */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { setMainNavigation } from "@insite/client-framework/Components/ShellHoleConnect";
import Logger from "@insite/client-framework/Logger";
import isEqual from "lodash/isEqual";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getCategoriesDataView,
    getCategoryDepthLoaded,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { StylesContext } from "@insite/content-library/additionalStyles";
import MainNavigationItem from "@NA/Overrides/Components/MainNavigationItem";
import SearchInput, { SearchInputStyles } from "@insite/content-library/Widgets/Header/SearchInput";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconProps, IconWrapper } from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { MenuPresentationProps } from "@insite/mobius/Menu";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import toggleLoginDrawer from "@NA/Handlers/ADILoginDrawerHandler";
import { PopoverPresentationProps, PositionStyle } from "@NA/Components/ADIHeaderPopover";
import { CustomCategoryModel } from "@insite/client-framework/Types/ApiModels";
import NavigationDrawer from "@NA/Overrides/Components/NavigationDrawer";
import ADIColors from "@NA/Utils/ADIColors";
import { pushAuthDataToDataLayer } from "@NA/Overrides/Common/Utilities/CustomTracking";
import isMobile from "ismobilejs";
import getProp from "@insite/mobius/utilities/getProp";

const enum fields {
    links = "links",
    showQuickOrder = "showQuickOrder",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkModel[];
        [fields.showQuickOrder]: boolean;
    };
}

interface LinkModel {
    fields: {
        openInNewWindow: boolean;
        isLoggedInRequired: boolean;
        mergedTwoCategoryIntoOne: boolean;
        megaMenuName: string;
        destination: LinkFieldValue;
        linkType: "Link" | "MegaMenu" | "CascadingMenu";
        overrideTitle: string;
        title: string;
        numberOfColumns: number;
        maxDepth: number;
    };
}

export interface MappedLink {
    categoryId?: string;
    title: string;
    url: string;
    type?: string;
    excludeFromNavigation?: boolean;
    openInNewWindow?: boolean;
    isLoggedInRequired?: boolean;
    mergedTwoCategoryIntoOne?: boolean;
    megaMenuName?: string;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu";
    children?: MappedLink[];
    properties?: any;
    newProductCategories?: CustomCategoryModel[] | null;
    shopProductSubCat?: CustomCategoryModel[] | null;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const {
        fields: { links },
    } = ownProps;
    let mappedLinks = [];
    const categoryIdsToLoad: Dictionary<number> = {};
    const newProductCategory = getCategoriesDataView(state).value?.filter(p => p?.name === "New Product")[0];
    for (const link of links) {
        const {
            fields: {
                linkType,
                destination,
                openInNewWindow,
                overrideTitle,
                numberOfColumns,
                maxDepth,
                title,
                isLoggedInRequired,
                mergedTwoCategoryIntoOne,
                megaMenuName,
            },
        } = link;
        const { type, value } = destination;

        const newProductSubCategories = newProductCategory?.subCategoryIds?.map(
            item => getCategoryState(state, item).value,
        );

        const depthToLoad = linkType === "MegaMenu" ? maxDepth : 3;

        let mappedLink: MappedLink | undefined;

        if (type === "Url") {
            mappedLink = {
                url: value,
                title,
            };
        } else if (type === "Page") {
            const readonlyPageLink = getPageLinkByNodeId(state, value);
            if (readonlyPageLink) {
                mappedLink = { ...readonlyPageLink };
                if (overrideTitle) {
                    mappedLink.title = overrideTitle;
                }
            }
        } else if (type === "Category") {
            mappedLink = setupCategoryLink(state, value, categoryIdsToLoad, depthToLoad, overrideTitle);
        }

        if (!mappedLink) {
            continue;
        }

        mappedLink.openInNewWindow = openInNewWindow;
        mappedLink.isLoggedInRequired = isLoggedInRequired;
        mappedLink.mergedTwoCategoryIntoOne = mergedTwoCategoryIntoOne;
        mappedLink.megaMenuName = megaMenuName;
        mappedLink.numberOfColumns = numberOfColumns;
        mappedLink.maxDepth = maxDepth;
        mappedLink.newProductCategories = newProductSubCategories as unknown as CustomCategoryModel[];

        if (linkType === "MegaMenu" || linkType === "CascadingMenu") {
            mappedLink.childrenType = linkType;
        }

        if(mappedLink.title === "Brands" && mappedLink.children )
        {
            const childLink : any = mappedLink?.children?.map(item=> {          
            if( item.title === "Shop All Brands" || item.title === "Featured Brands"){
                return {...item,children:null};
            } 
            return item;
          }).filter(item=>{
            return ["Shop Products","Deals Center"].includes(item.title)===false;
          })
            mappedLinks.push({...mappedLink, children: childLink});
        } else {
            mappedLinks.push(mappedLink);
        }
    }

    let customerNumber = "";
    const billToId = state.context.session?.billToId;
    if (billToId) {
        customerNumber = state.data.billTos.byId[billToId]?.customerNumber;
    }

    mappedLinks = mappedLinks.map(eachLink => {
        if (eachLink.title === "Brands") {
            return {
                ...eachLink,
                children: eachLink?.children?.filter((eachChild : any) => {
                    return eachChild.url !== "/catalog/shopproducts";
                }),
            };
        }
        return eachLink;
    });

    return {
        links: mappedLinks,
        quickOrderLink: getPageLinkByPageType(state, "QuickOrderPage"),
        categoryIdsToLoad,
        allowQuickOrder: getSettingsCollection(state).orderSettings.allowQuickOrder,
        categoryErrorStatusCodeById: state.data.categories.errorStatusCodeById,
        displayChangeCustomerLink: state.context?.session?.displayChangeCustomerLink,
        session: state.context.session,
        categories: state.data.categories,
        categoriesDataView: getCategoriesDataView(state),
        newProductCategory,
        customerNo: customerNumber,
    };
};

const getParameter = (categoryId: string, maxDepth: number) => ({
    maxDepth,
    startCategoryId: categoryId === emptyGuid ? undefined : categoryId,
    includeStartCategory: categoryId !== emptyGuid,
});

const setupCategoryLink = (
    state: ApplicationState,
    value: string,
    categoryIdsToLoad: Dictionary<number>,
    maxDepth: number,
    overrideTitle: string,
) => {
    let mappedLink: MappedLink | undefined;

    if (!value) {
        return;
    }

    const depthLoaded = getCategoryDepthLoaded(state, value);
    const subCategoryIds = getSubCategoryIds(state, value);
    if (
        (depthLoaded < maxDepth || !subCategoryIds) &&
        !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
    ) {
        categoryIdsToLoad[value] = maxDepth;
    } else {
        if (value === emptyGuid) {
            mappedLink = {
                url: "",
                title: overrideTitle || translate("Products"),
            };
        } else {
            const categoryState = getCategoryState(state, value);

            if (!categoryState.value) {
                if (
                    !categoryState.isLoading &&
                    !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
                ) {
                    categoryIdsToLoad[value] = maxDepth;
                }
                return;
            }

            mappedLink = {
                url: categoryState.value.path,
                title: overrideTitle || categoryState.value.shortDescription,
                properties: categoryState.value.properties,
            };
        }

        loadSubCategories(mappedLink, subCategoryIds, 1, maxDepth, state);
    }

    return mappedLink;
};

const loadSubCategories = (
    mappedLink: MappedLink,
    subCategoryIds: readonly string[] | undefined,
    currentDepth: number,
    maxDepth: number,
    state: ApplicationState,
) => {
    if (currentDepth > maxDepth) {
        return;
    }
    mappedLink.children = [];

    if (!subCategoryIds) {
        return;
    }

    for (const subCategoryId of subCategoryIds) {
        const subCategory = getCategoryState(state, subCategoryId).value;

        if (!subCategory) {
            Logger.error(`Category ${subCategoryId} was not found in data state, excluding from navigation.`);
            continue;
        }
        const childLink: MappedLink = {
            categoryId: subCategory.id,
            url: subCategory.path,
            title: subCategory.shortDescription,
            children: [],
            properties: subCategory.properties,
        };
        mappedLink.children.push(childLink);

        loadSubCategories(childLink, subCategory.subCategoryIds, currentDepth + 1, maxDepth, state);
    }
};

const mapDispatchToProps = {
    loadCategories,
    toggleLoginDrawer,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface State {
    selectedLinkIndex?: number;
    mobileSearchModalIsOpen: boolean;
}

export interface MainNavigationStyles {
    menuHeight: number;
    container?: InjectableCss;
    itemWrapper?: InjectableCss;
    quickOrderItemWrapper?: InjectableCss;
    menuItem?: InjectableCss;
    menuItemIcon?: IconProps;
    menuItemTypography?: TypographyPresentationProps;
    cascadingMenu?: MenuPresentationProps;
    megaMenu?: PopoverPresentationProps;
    megaMenuGridContainer?: GridContainerProps;
    megaMenuGridItem?: GridItemProps;
    megaMenuHeading?: LinkPresentationProps;
    megaMenuLink?: LinkPresentationProps;
    mobileWrapper?: InjectableCss;
    desktopWrapper?: InjectableCss;
    mobileMenuWrapper?: InjectableCss;
    mobileSearchButton?: ButtonPresentationProps;
    mobileSearchWrapper?: InjectableCss;
    mobileSearchModal?: ModalPresentationProps;
    mobileSearchInputWrapper?: InjectableCss;
    mobileSearchInputStyles?: SearchInputStyles;
    mobileSearchModalCloseIcon?: IconProps;
}

export const mainNavigationStyles: MainNavigationStyles = {
    menuHeight: 50,
    container: {
        css: css`
            color: ${getColor("common.background")};
            padding: 0px;
            display: flex;
            justify-content: left;

            .content-wrap-deals {
                display: flex; 
                .heading{
                    font-weight: bold;
                }
                .label{
                    font-size: 12px;
                    margin-bottom:0;
                }
                .heading,.btn-link{
                    font-size: 12px;
                    &:hover span{
                        color: #006ae3;
                    }
                    span{
                        color: #2d2926;
                    }
                }
                .btn-link span{
                    text-decoration: underline;
                }
            }
            .second-services{
                flex-wrap:wrap;
                
                .content-wrap-deals-dv {
                    max-width: 200px;
                }
            }
            .content-wrap-deals .content-wrap-deals-dv {
                max-width: 254px;
                margin-right:25px;
            }
        `,
    },
    itemWrapper: {
        css: css`
            position: unset;
            overflow: visible;
            display: flex;
            /* padding-bottom: 30px;
            margin-top: 30px; */
            &:hover span + div {
                display: block;
            }
            /* &:first-child {
                margin-left: -10px;
            } */

            &:nth-child(n-1) > button > span {
                margin: 0px;
            }
        `,
    },
    quickOrderItemWrapper: {
        css: css`
            margin-left: auto;
            margin-right: 0;
        `,
    },
    megaMenuGridContainer: {
        css: css`
            margin: 0 auto;
        `,
        offsetCss: css`
            display: flex;
            flex-direction: row;
            padding: 0px;
            border-radius: 0;
            margin: 0px;
            width: 100% !important;
            height: auto;
        `,
    },
    megaMenuGridItem: {
        css: css`
            display: inline-block !important;
            padding: 10px;
            line-height: 1.125;
            padding: 0.4rem 0rem;
            width: 100%;
            max-width: 100%;
        `,
    },
    megaMenuHeading: {
        color: "secondary",
        css: css`
            /* font-family: "sans-serif"; */
            margin-bottom: 0.4rem;
        `,
        typographyProps: {
            size: "14px",
            underline: true,
            css: css`
                &:hover {
                    color: ${getColor("primary")} !important;
                }
            `,
        },
    },
    menuItem: {
        css: css`
            display: flex;
            align-items: center;
            /* height: 80px; */
            /* padding: 0 8px; */
            position: relative;
            font-weight: 500;
            line-height: normal;
            cursor: default;
            letter-spacing: normal;
            text-decoration: none;
            margin: 0px 22px 0px 0px;
            &:focus {
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, null, null,
                    css` 
                             outline-color:  ${ADIColors.darkBlack3};      
                             outline-style: solid;
                             outline-width: 0px;
                            `,],
                    "min",);
            }}                  
            }
            &:focus-visible {
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, null, null,
                    css` 
                        outline-color: ${ADIColors.darkBlack3};    
                        outline-style: ${getProp("theme.focus.style", "solid")};
                        outline-width: 1px;
                        outline-offset: 1px; `,],
                    "min",);
            }}
            } 
            > span {
                color: ${ADIColors.darkBlue1}!important;
                cursor: pointer;
                font-size: 14px;
                margin: 0px ;
                line-height: normal;
                letter-spacing: normal;
                font-weight: 600;
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [
                    null,
                    null,
                    null,
                    css` 
                        position: relative; /* Do not override or modify. Positioning of diagonal mega menu */
                    `,], "min",);
            }}
            }
            &.menu-item.activeTab {
                text-decoration: underline ${getColor("text.main")} 4px;
                text-underline-offset: 20px;
            }
            /* &.menu-item.activetab {
                text-decoration: underline #020508 4px;
            } */
            ${IconWrapper} {
                margin: -3px 0 0 3px !important;
                width: 18px !important;
                height: 18px !important;

                svg {
                    width: 18px !important;
                    height: 13px !important;
                    font-weight: bold;
                    color: ${ADIColors.darkBlue1}!important;
                    stroke-width: 3px;
                }
            }
        `,
    },
    menuItemIcon: {
        css: css`
            margin: -3px 0 0 10px;
        `,
        size: 18,
        color: "secondary.contrast",
    },
    menuItemTypography: {
        variant: "headerSecondary",
        color: "primary",
        // fontFamily: "sans-serif",
        css: css`
            font-size: 15px;
            text-transform: uppercase;
            font-weight: 500;
            margin: 0 17px 0 0;
        `,
    },
    megaMenu: {
        transitionDuration: "short",
        contentBodyProps: {
            _height: "1200px",
            css: css`
                padding: 1rem;
                max-width: 100% !important;
                height: auto;
                overflow: hidden !important;
                
            `,
        },
    },
    mobileSearchButton: {
        size: 48,
        buttonType: "solid",
        color: "secondary",
        css: css`
            padding: 0 10px;
        `,
    },
    mobileWrapper: {
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: flex;
                            justify-content: space-between;
                        `,
                        css`
                            display: flex;
                            justify-content: space-between;
                        `,
                        css`
                            display: flex;
                            justify-content: space-between;
                        `,
                        null,
                        css`
                            display: none;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    desktopWrapper: {
        css: css`
        &:focus {
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, null, null,
                    css` 
                        outline-color:  ${getProp("theme.focus.color", "#09f")};       
                        outline-style: solid;
                        outline-width: 0px;
                        `,],
                    "min",);
            }}                  
        }
        &:focus-visible {
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, null, null,
                    css` 
                    outline-color: ${getProp("theme.focus.color", "#09f")};     
                    outline-style: ${getProp("theme.focus.style", "solid")};
                    outline-width: 1px;
                    outline-offset: 1px; `,],
                    "min",);
            }}
        } 
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                        css`
                            display: none;
                        `,
                        css`
                            display: none;
                        `,
                        css`
                            display: block;
                        `,
                        css`
                            display: block;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    mobileSearchWrapper: {
        css: css`
            text-align: right;
        `,
    },
    mobileMenuWrapper: {
        css: css`
            text-align: right;
        `,
    },
    mobileSearchInputWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    mobileSearchInputStyles: {
        input: {
            cssOverrides: {
                formField: css`
                    width: 100%;
                `,
            },
        },
        popover: {
            wrapperProps: {
                css: css`
                    width: calc(100% - 45px);
                `,
            },
        },
        popoverContentBody: {
            as: "div",
            _height: "100%",
            css: css`
                box-shadow: none;
            `,
        },
        autocompleteWrapper: {
            css: css`
                display: flex;
                flex-direction: column;
            `,
        },
        autocompleteColumnWrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                width: 100%;
                padding: 0 50px 0 30px;
            `,
        },
        searchHistoryStyles: {
            wrapper: {
                css: css`
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                    padding: 0 50px 15px 30px;
                `,
            },
        },
        autocompleteProductsStyles: {
            titleLink: {
                css: css`
                    width: 100%;
                `,
            },
            erpNumberText: {
                css: css`
                    width: 100%;
                    text-align: left;
                    margin: 5px 0 0 0;
                `,
            },
        },
    },
    mobileSearchModalCloseIcon: {
        src: X,
        size: 18,
        css: css`
            margin-left: 10px;
        `,
    },
};

export class MainNavigation extends React.Component<Props, State> {
    container = React.createRef<HTMLDivElement>();
    mobileSearchInput = React.createRef<HTMLInputElement>();

    constructor(props: Props) {
        super(props);

        this.state = {
            mobileSearchModalIsOpen: false,
        };
    }

    UNSAFE_componentWillMount(): void {
        this.loadCategoriesIfNeeded();
        setMainNavigation({
            close: () => {
                this.setState({
                    selectedLinkIndex: undefined,
                });
            },
            openMenu: (index: number) => {
                this.setState({
                    selectedLinkIndex: index,
                });
            },
        });

        const { categoriesDataView, loadCategories } = this.props;

        if ( !categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories();
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (!isEqual(this.props.categoryIdsToLoad, prevProps.categoryIdsToLoad)) {
            this.loadCategoriesIfNeeded(true);
        }
        if (!isEqual(this.props.customerNo, prevProps.customerNo)) {
            pushAuthDataToDataLayer(this.props.customerNo, this.props.session);
        }
    }
    loadCategoriesIfNeeded(isComponentDidUpdate = false) {
        const { categoryIdsToLoad } = this.props;
        for (const categoryId of Object.keys(categoryIdsToLoad)) {
            if (!this.props.categoryErrorStatusCodeById?.[categoryId]) {
                const maxDepth = categoryIdsToLoad[categoryId];
                this.props.loadCategories(getParameter(categoryId, maxDepth));
            }
        }
        // this.props.loadCategories(getParameter("5ee4540b-9d7b-497b-a794-af4700be46ac", 3));
    }

    getMobileSearchAutocompletePosition = (element: React.RefObject<HTMLUListElement>) => {
        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top = rect.top + rect.height;
            positionStyle.left = 0;
            positionStyle.width = `${document.documentElement.clientWidth}px`;
            positionStyle.height = `${document.documentElement.clientHeight - positionStyle.top}px`;
        }

        return positionStyle;
    };

    mobileSearchButtonClickHandler = () => {
        this.setState({ mobileSearchModalIsOpen: true }, () => {
            setTimeout(() => {
                if (this.mobileSearchInput?.current) {
                    this.mobileSearchInput.current.focus();
                    this.mobileSearchInput.current.click();
                }
            }, 250);
        });
    };

    mobileSearchModalCloseHandler = () => {
        this.setState({ mobileSearchModalIsOpen: false });
    };

    render() {
        const {
            links,
            quickOrderLink,
            fields: { showQuickOrder },
            id,
            allowQuickOrder,
            session,
            categories,
            categoriesDataView,
            toggleLoginDrawer,
            newProductCategory,
        } = this.props;
        const { selectedLinkIndex } = this.state;
        const showQuickOrderLink = showQuickOrder && allowQuickOrder;
        const mainNavContextStyles = this?.context?.styles?.mainNavigation ?? {};
        const styles = mergeToNew(mainNavigationStyles, mainNavContextStyles);
        const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;

        return (
            <>
                <StyledWrapper {...styles.mobileWrapper}>
                    <Hidden above="md" {...styles.mobileMenuWrapper}>
                        <NavigationDrawer
                            newProductCategory={newProductCategory as unknown as CustomCategoryModel}
                            links={links}
                            showQuickOrder={showQuickOrderLink}
                            quickOrderLink={quickOrderLink}
                        />
                    </Hidden>
                </StyledWrapper>
                <Hidden {...styles.desktopWrapper} tabIndex={0}>
                    <StyledWrapper {...styles.container} ref={this.container}>
                        {links.map((link, index) => {
                            return (
                                // eslint-disable-next-line react/no-array-index-key
                                <StyledWrapper {...styles.itemWrapper} key={`${index}${link.title}`}>
                                    <MainNavigationItem
                                        zoneId={id}
                                        index={index}
                                        link={link}
                                        styles={styles}
                                        container={this.container}
                                        isOpen={index === selectedLinkIndex}
                                        isAuth={isAuthenticated}
                                        toggleLogin={toggleLoginDrawer}
                                        displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                                    />
                                </StyledWrapper>
                            );
                        })}
                        {showQuickOrderLink && quickOrderLink && (
                            <StyledWrapper {...styles.itemWrapper}>
                                <StyledWrapper {...styles.quickOrderItemWrapper}>
                                    <Link
                                        typographyProps={styles.menuItemTypography}
                                        color={styles.menuItemTypography?.color}
                                        {...styles.menuItem}
                                        id="quickOrder"
                                        href={quickOrderLink.url}
                                    >
                                        {quickOrderLink.title}
                                    </Link>
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </Hidden>
                <Modal
                    {...styles.mobileSearchModal}
                    isOpen={this.state.mobileSearchModalIsOpen}
                    closeOnEsc={true}
                    handleClose={this.mobileSearchModalCloseHandler}
                    headline={
                        <SearchInput
                            id={id}
                            inputRef={this.mobileSearchInput}
                            autocompletePositionFunction={this.getMobileSearchAutocompletePosition}
                            onBeforeGoToUrl={this.mobileSearchModalCloseHandler}
                            extendedStyles={styles.mobileSearchInputStyles}
                        />
                    }
                />
            </>
        );
    }
}

MainNavigation.contextType = StylesContext;

const mainNavigation: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MainNavigation),
    definition: {
        group: "Header",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                onEditRow: (index: number, dispatch) => {
                    dispatch({
                        type: "SendToSite/OpenMainNavigation",
                        index,
                    });
                },
                onDoneEditingRow: dispatch => {
                    dispatch({
                        type: "SendToSite/CloseMainNavigation",
                    });
                },
                onLoad: dispatch => {
                    dispatch({
                        type: "SendToSite/CloseMainNavigation",
                    });
                },
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const { overrideTitle, title } = item.fields;
                    const disabledMegaMenuName = item.fields;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overrideTitle || link?.title;
                    }
                    if (type === "Category") {
                        if (overrideTitle) {
                            return overrideTitle;
                        }
                        if (value === emptyGuid) {
                            return "Products";
                        }
                        const categoryState = getCategoryState(state, value);
                        if (!categoryState.value) {
                            if (!categoryState.isLoading && value) {
                                return dispatch => {
                                    dispatch(
                                        loadCategory({
                                            id: value,
                                        }),
                                    );
                                };
                            }
                            return "";
                        }
                        return categoryState.value.shortDescription;
                    }
                    if (type === "Url") {
                        return title ?? value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "linkType",
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "Link",
                        isRequired: true,
                        options: [
                            {
                                displayName: "Link",
                                value: "Link",
                            },
                            {
                                displayName: "Mega Menu",
                                value: "MegaMenu",
                            },
                            {
                                displayName: "Cascading Menu",
                                value: "CascadingMenu",
                            },
                        ],
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowUrls: item => item.fields.linkType === "Link",
                        showCategoryRoot: item => true,
                    },
                    {
                        name: "overrideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => item.fields.destination.type !== "Url",
                    },
                    {
                        name: "megaMenuName",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: (item: HasFields) =>
                            item.fields.linkType === "CascadingMenu" || item.fields.linkType === "MegaMenu",
                        isEnabled: item => item?.fields.disabledMegaMenuName === false,
                    },
                    {
                        name: "disabledMegaMenuName",
                        displayName: "Please Don't Click",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                        tooltip:
                            "(By checking the checkbox, the Mega Menu Name field will be enabled and a distinct value can be set that can be used to uniquely identify a specific menu. If the mega menu name field is already populated please don't change it.)",
                    },
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                        isVisible: item => item.fields.destination.type === "Url",
                    },
                    {
                        name: "numberOfColumns",
                        editorTemplate: "DropDownField",
                        defaultValue: 6,
                        options: [{ value: 2 }, { value: 3 }, { value: 4 }, { value: 6 }],
                        isRequired: true,
                        isVisible: (item: HasFields) => item.fields.linkType === "MegaMenu",
                        isEnabled: () => false,
                    },
                    {
                        name: "maxDepth",
                        editorTemplate: "DropDownField",
                        defaultValue: 3,
                        options: [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }],
                        isRequired: true,
                        isVisible: (item: HasFields) =>
                            item.fields.linkType === "CascadingMenu" || item.fields.linkType === "MegaMenu",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: (item: HasFields) => item.fields.linkType === "Link",
                    },
                    {
                        name: "isLoggedInRequired",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: (item: HasFields) => item.fields.linkType === "Link",
                    },
                    {
                        name: "mergedTwoCategoryIntoOne",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: (item: HasFields) =>
                            item.fields.linkType === "CascadingMenu" || item.fields.linkType === "MegaMenu",
                    },
                ],
            },
            {
                name: fields.showQuickOrder,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default mainNavigation;
